import { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { SignUpLayout } from '../templates/SignUpLayout';
import { LogInForm } from '../organisms/LogInForm';
import { useAlert } from '../../contexts/AlertContext';
import { getSelf } from '../../api/user';


const LogIn: FC = () => {
  const alert = useAlert();
  const navigate = useNavigate();
  const { signInWithEmailAndPassword } = useAuth();

  const logInIfUserExists = useCallback(async (user?: User) => {
    const errorMessage = 'User doesn\'t exist';

    if (user) {
      try {
        const token = await user.getIdToken();

        const requestConfig = {
          headers: { Authorization: `Bearer ${token}` },
        };

        const self = await getSelf(requestConfig);

        if (!self) {
          alert?.error(errorMessage);
          return;
        }

        if (!self.companyId && !self.isAdmin) {
          // if (!user.emailVerified) {
          //   navigate('/email-verification');
          //   return;
          // }

          navigate('/sign-up/company');
          return;
        }

        navigate('/dashboard');
      } catch (err) {
        alert?.displayErrorAlert(err);
      }
    } else {
      alert?.error(errorMessage);
    }
  }, [alert, navigate]);

  const handleSubmit = useCallback(async ({
    email,
    password
  }: { email: string; password: string }) => {
    try {
      const userCredentials = await signInWithEmailAndPassword({ email, password });

      await logInIfUserExists(userCredentials.user);
    } catch (err) {
      alert?.displayErrorAlert(err);
    }
  }, [alert, logInIfUserExists, signInWithEmailAndPassword]);

  const onGoogleSignInSuccess = useCallback(async (user?: User) => {
    await logInIfUserExists(user);
  }, [logInIfUserExists]);

  const canonicalTag = useMemo(() => {
    const isProduction = process.env.REACT_APP_STAGE === 'prod';
    if (!isProduction) {
      return;
    }

    return (
      <link rel="canonical" href={`${window.location.origin}/login`} />
    );
  }, []);

  return (
    <>
      <Helmet>
        <title>Login PriceAgent | Price Optimization Software</title>
        {canonicalTag}
      </Helmet>

      <SignUpLayout
        title="Login"
        bottomSectionConfig={{
          message: 'Don’t have an account?',
          linkText: 'Sign up',
          linkHref: '/sign-up',
        }}
      >
        <LogInForm handleSubmit={handleSubmit} onGoogleSignInSuccess={onGoogleSignInSuccess} />
      </SignUpLayout>
    </>
  );
};

export default LogIn;
